/** Test setup: SAT window, settings, commitments, layout review/editor, clock offsets. */
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { api, ApiError } from "../api/client";
import { keys, useBatch, useCommitments, useLayout, useMe, useProgramme, useProject } from "../api/hooks";
import type { CommitmentsView, LayoutDoc, Manifest, Run, SiteLayout } from "../api/types";
import { can } from "../components/AppShell";
import { hasClockOffsets, LayoutEditor, layoutFingerprint } from "../components/LayoutEditor";
import { StageIntro } from "../components/sat";
import { ConfirmDialog, ErrorNotice, Field, Notice, Spinner } from "../components/ui";
import { dateTime, localText, num, yearLabel } from "../lib/format";

const C_RATES: [number, number][] = [
  [1.0, 2.0],
  [0.75, 2.67],
  [0.5, 4.0],
  [0.25, 8.0],
];

// ------------------------------------------------------------------ commitments

function CommitmentsPanel({ pid, year, editable }: { pid: string; year: number | null; editable: boolean }) {
  const commitments = useCommitments(pid, year);
  const client = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ nameplate_mwh: "", cuep_pct: "", crte_pct: "", committed_cue_mwh: "", reason: "" });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  if (year === null) {
    return (
      <p className="muted small">
        The reporting year is inferred from the SAT window and the commissioning date. Its commitments come from the guarantee (or a saved
        override) and are shown with the result. Choose a year to review or override them here.
      </p>
    );
  }
  if (commitments.isLoading) return <Spinner label="Loading commitments" />;
  if (commitments.error) return <ErrorNotice error={commitments.error} />;
  const view = commitments.data!;
  const resolved = view.resolved as Record<string, any> | null | undefined;
  const override = view.override as Record<string, any> | null | undefined;

  async function save(body: { version: number; override: Record<string, unknown> | null }) {
    setBusy(true);
    setError(null);
    try {
      const updated = await api.put<CommitmentsView>(`/projects/${pid}/commitments/${year}`, body);
      client.setQueryData(keys.commitments(pid, year!), updated);
      await client.invalidateQueries({ queryKey: keys.programme(pid) });
      setEditing(false);
    } catch (reason) {
      setError(reason);
    } finally {
      setBusy(false);
    }
  }

  const numberOrNull = (value: string) => (value.trim() === "" ? null : Number(value));
  return (
    <div className="stack">
      {!resolved ? (
        <Notice kind="warning">The guarantee has no commitments for {yearLabel(year)}.</Notice>
      ) : (
        <div className="grid-4">
          <div className="metric">
            <span>Nameplate energy (NE)</span>
            <strong>{num(resolved.nameplate_mwh)} MWh</strong>
          </div>
          <div className="metric">
            <span>Committed CUEP</span>
            <strong>{num(resolved.cuep_pct)}%</strong>
          </div>
          <div className="metric">
            <span>Committed CRTE</span>
            <strong>{num(resolved.crte_pct)}%</strong>
            <small>Acceptance less {num(resolved.meter_allowance_pp, 1)} pp meter allowance</small>
          </div>
          <div className="metric">
            <span>Committed CUE</span>
            <strong>{num(resolved.committed_cue_mwh)} MWh</strong>
          </div>
        </div>
      )}
      {override ? (
        <Notice kind="warning" title={`Override in force for ${yearLabel(year)}`}>
          {String(override.reason)}
        </Notice>
      ) : (
        <p className="muted small">Source: performance guarantee{resolved?.cpd ? ` (CPD ${resolved.cpd})` : ""}. Values never change unless an override is saved with a reason.</p>
      )}
      {editable && !editing && (
        <div className="row">
          <button
            className="btn small"
            onClick={() => {
              setForm({
                nameplate_mwh: override?.nameplate_mwh?.toString() ?? "",
                cuep_pct: override?.cuep_pct?.toString() ?? "",
                crte_pct: override?.crte_pct?.toString() ?? "",
                committed_cue_mwh: override?.committed_cue_mwh?.toString() ?? "",
                reason: override?.reason ?? "",
              });
              setEditing(true);
            }}
          >
            {override ? "Edit override" : "Override for this year"}
          </button>
          {override && (
            <ConfirmDialog
              trigger={<button className="btn small danger">Remove override</button>}
              title="Remove the override?"
              description={`${yearLabel(year)} goes back to the guarantee values.`}
              confirmLabel="Remove override"
              danger
              onConfirm={() => save({ version: view.version, override: null })}
            />
          )}
        </div>
      )}
      {editing && (
        <form
          className="card stack"
          onSubmit={(event) => {
            event.preventDefault();
            void save({
              version: view.version,
              override: {
                reason: form.reason,
                nameplate_mwh: numberOrNull(form.nameplate_mwh),
                cuep_pct: numberOrNull(form.cuep_pct),
                crte_pct: numberOrNull(form.crte_pct),
                committed_cue_mwh: numberOrNull(form.committed_cue_mwh),
              },
            });
          }}
        >
          <p className="muted small">Fill only the values that differ from the guarantee. The reason is shown on the run and in the reports.</p>
          <div className="grid-4">
            {(
              [
                ["nameplate_mwh", "NE (MWh)"],
                ["cuep_pct", "CUEP (%)"],
                ["crte_pct", "CRTE (%)"],
                ["committed_cue_mwh", "Committed CUE (MWh)"],
              ] as const
            ).map(([field, label]) => (
              <Field label={label} key={field}>
                <input className="input" type="number" step="any" min={0} value={form[field]} onChange={(event) => setForm({ ...form, [field]: event.target.value })} />
              </Field>
            ))}
          </div>
          <Field label="Reason (required)">
            <textarea className="input" required maxLength={500} value={form.reason} onChange={(event) => setForm({ ...form, reason: event.target.value })} />
          </Field>
          <ErrorNotice error={error} />
          <div className="row end">
            <button type="button" className="btn" onClick={() => setEditing(false)}>
              Cancel
            </button>
            <button className="btn primary" disabled={busy}>
              {busy ? "Saving…" : "Save override"}
            </button>
          </div>
        </form>
      )}
      {!editing && <ErrorNotice error={error} />}
    </div>
  );
}

// ------------------------------------------------------------------ layout

function LayoutPanel(props: { pid: string; manifest: Manifest; saved: LayoutDoc | null; onSaved: () => void }) {
  const { pid, manifest, saved } = props;
  const client = useQueryClient();
  const proposal = manifest.proposal;
  const savedLayout = saved ? (saved.layout as unknown as SiteLayout) : null;
  const differs = !!savedLayout && layoutFingerprint(savedLayout) !== layoutFingerprint(proposal.layout);
  const [draft, setDraft] = useState<SiteLayout | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const diff = proposal.diff;
  const diffCount = diff.new_serials.length + diff.missing_serials.length + diff.moved_serials.length + diff.new_loggers.length;

  async function put(layout: SiteLayout, kind: "user" | "auto-accepted") {
    setBusy(true);
    setError(null);
    try {
      const doc = await api.put<LayoutDoc>(`/projects/${pid}/layout`, { layout, version: saved?.version ?? null, kind, note });
      client.setQueryData(keys.layout(pid), doc);
      setDraft(null);
      setNote("");
      props.onSaved();
    } catch (reason) {
      setError(reason);
    } finally {
      setBusy(false);
    }
  }

  const conflict = error instanceof ApiError && error.code === "version_conflict";
  const dirty = draft !== null && (!savedLayout || layoutFingerprint(draft) !== layoutFingerprint(savedLayout));
  const noteNeeded = draft !== null && hasClockOffsets(draft) && note.trim() === "";

  return (
    <div className="stack">
      {manifest.clock_offset_warnings.map((warning) => (
        <Notice kind="warning" key={warning.section} title="Check the SmartLogger clock">
          {warning.message}
        </Notice>
      ))}
      {proposal.warnings.map((warning) => (
        <Notice kind="warning" key={warning}>
          {warning}
        </Notice>
      ))}
      {!saved && !draft && (
        <Notice kind="info" title="No layout is saved for this project yet">
          The sections below were read from the SmartLogger serial in each battery export. Battery positions inside a section are not in the
          data: they are proposed in serial order and marked unconfirmed until you confirm them.
        </Notice>
      )}
      {saved && !differs && !draft && (
        <p style={{ margin: 0 }}>
          <strong>Layout: v{saved.layout_version}</strong> <span className="muted">(unchanged)</span>
          {saved.saved_at ? <span className="muted small"> · saved {dateTime(saved.saved_at, manifest.timezone)}</span> : null}
        </p>
      )}
      {saved && differs && !draft && (
        <Notice kind="warning" title={`This evidence differs from the saved layout (v${saved.layout_version})`}>
          {diffCount === 0 ? "Labels, positions or clock offsets differ." : null}
          {diff.new_serials.length > 0 && <div>New batteries: {diff.new_serials.join(", ")}</div>}
          {diff.missing_serials.length > 0 && <div>Not in this evidence: {diff.missing_serials.join(", ")}</div>}
          {diff.moved_serials.map((moved) => (
            <div key={moved.serial}>
              {moved.serial} moved from section {moved.from} to {moved.to}
            </div>
          ))}
          {diff.new_loggers.length > 0 && <div>New SmartLoggers: {diff.new_loggers.join(", ")}</div>}
          <div className="small" style={{ marginTop: "0.4rem" }}>
            The analysis uses the saved layout. Review the proposal to update it.
          </div>
        </Notice>
      )}
      <LayoutEditor layout={draft ?? savedLayout ?? proposal.layout} onChange={setDraft} disabled={!draft || busy} />
      {draft && (
        <Field label="Note" hint={hasClockOffsets(draft) ? "Required with a clock offset: why is the logger clock corrected?" : "Optional. Stored with this layout version."}>
          <input className="input" value={note} maxLength={500} onChange={(event) => setNote(event.target.value)} />
        </Field>
      )}
      <ErrorNotice error={error} title={conflict ? "Someone else saved the layout in the meantime" : undefined} />
      <div className="row end">
        {conflict && (
          <button
            className="btn"
            onClick={async () => {
              setError(null);
              setDraft(null);
              await client.invalidateQueries({ queryKey: keys.layout(pid) });
            }}
          >
            Reload the saved layout
          </button>
        )}
        {!draft && !saved && (
          <button className="btn primary" disabled={busy || proposal.layout.unassigned.length > 0} onClick={() => put(proposal.layout, "auto-accepted")}>
            {busy ? "Saving…" : "Accept layout"}
          </button>
        )}
        {!draft && (
          <button className="btn" onClick={() => setDraft(structuredClone(savedLayout ?? proposal.layout))}>
            Edit layout
          </button>
        )}
        {!draft && saved && differs && (
          <button className="btn" onClick={() => setDraft(structuredClone(proposal.layout))}>
            Review the proposal
          </button>
        )}
        {draft && (
          <>
            <button className="btn" disabled={busy} onClick={() => { setDraft(null); setError(null); }}>
              Discard changes
            </button>
            <button className="btn primary" disabled={busy || !dirty || noteNeeded} onClick={() => put(draft, "user")}>
              {busy ? "Saving…" : "Save layout"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ page

export function SetupPage() {
  const { pid = "", bid = "" } = useParams();
  const me = useMe();
  const project = useProject(pid);
  const batch = useBatch(bid);
  const programme = useProgramme(pid);
  const editor = can(me.data, "run.create");
  const layout = useLayout(pid, editor);
  const navigate = useNavigate();
  const client = useQueryClient();
  const manifest = batch.data?.manifest as Manifest | null | undefined;

  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [requiredCycles, setRequiredCycles] = useState(1);
  const [cRate, setCRate] = useState(0.25);
  const [minSwing, setMinSwing] = useState("96");
  const [threshold, setThreshold] = useState("5");
  const [year, setYear] = useState<number | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<unknown>(null);

  // Pre-fill once per processing from the detected window (B8: there is no fallback window).
  const prefillKey = manifest ? `${manifest.batch_id}:${manifest.processed}` : "";
  useEffect(() => {
    if (!manifest) return;
    const window = manifest.detection ?? manifest.data_range;
    setStart(window ? window.start_local.slice(0, 16) : "");
    setEnd(window ? window.end_local.slice(0, 16) : "");
    setRequiredCycles(manifest.detection?.required_cycles ?? 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillKey]);

  const years = useMemo(() => programme.data?.years.map((item) => item.year) ?? [], [programme.data]);

  if (me.data && !editor) return <Notice kind="info">The test setup is done by the project team.</Notice>;
  if (batch.isLoading || project.isLoading) return <Spinner label="Loading the test setup" />;
  if (batch.error || project.error) return <ErrorNotice error={batch.error ?? project.error} />;
  if (!manifest) {
    return (
      <Notice kind="warning" title="This evidence has not been processed yet">
        <Link to={`/p/${pid}/evidence?b=${bid}`}>Go back to the evidence</Link> and process it first.
      </Notice>
    );
  }

  const layoutMissing = layout.error instanceof ApiError && layout.error.status === 404;
  const saved = layoutMissing ? null : (layout.data ?? null);
  const timezoneChanged = manifest.timezone !== project.data!.timezone;
  const boundary = project.data!.boundary as { label?: string } | undefined;
  const windowValid = start !== "" && end !== "" && end > start;
  const canRun = !!saved && windowValid && !timezoneChanged && manifest.batteries > 0 && !running;

  async function run() {
    setRunning(true);
    setError(null);
    try {
      const created = await api.post<Run>(`/batches/${bid}/runs`, {
        settings: {
          sat_start: start,
          sat_end: end,
          required_cycles: requiredCycles,
          c_rate: cRate,
          min_valid_swing: Number(minSwing),
          soc_deviation_threshold: Number(threshold),
        },
        ...(year !== null ? { reporting_year: year } : {}),
      });
      client.setQueryData(keys.run(created.id), created);
      await client.invalidateQueries({ queryKey: keys.runs(pid) });
      navigate(`/p/${pid}/runs/${created.id}`);
    } catch (reason) {
      setError(reason);
      setRunning(false);
    }
  }

  return (
    <div className="stack" style={{ gap: "1.4rem" }}>
      <StageIntro
        title="Confirm the test setup"
        description="Nothing here is saved silently: the window and settings apply to this run only, the layout and overrides are saved explicitly."
        status={`${manifest.batteries} batteries`}
      />
      {timezoneChanged && (
        <Notice kind="error" title="The project time zone changed">
          This evidence was processed in {manifest.timezone}. <Link to={`/p/${pid}/evidence?b=${bid}`}>Process it again</Link> before running the analysis.
        </Notice>
      )}

      <section className="card stack">
        <div className="card-head">
          <div>
            <h3>SAT window</h3>
            <p>
              Project local time ({manifest.timezone}).{" "}
              {manifest.detection?.auto_detected
                ? `Detected: ${localText(manifest.detection.start_local)} – ${localText(manifest.detection.end_local)}.`
                : "No window was detected automatically; the data range is pre-filled."}
            </p>
          </div>
          <span className="sat-boundary-badge">{boundary?.label ?? "Measurement boundary"}</span>
        </div>
        <div className="grid-2">
          <Field label="Start">
            <input className="input" type="datetime-local" required value={start} onChange={(event) => setStart(event.target.value)} />
          </Field>
          <Field label="End">
            <input className="input" type="datetime-local" required value={end} onChange={(event) => setEnd(event.target.value)} />
          </Field>
        </div>
        {!windowValid && <Notice kind="warning">Set a SAT window that ends after it starts.</Notice>}
        {manifest.data_range && (
          <p className="muted small" style={{ margin: 0 }}>
            The evidence covers {localText(manifest.data_range.start_local)} – {localText(manifest.data_range.end_local)}.
          </p>
        )}
      </section>

      <section className="card stack">
        <div className="card-head">
          <div>
            <h3>Test settings</h3>
            <p>Defaults follow the guarantee's test procedure.</p>
          </div>
        </div>
        <div className="grid-4">
          <Field label="Required full cycles">
            <select className="input" value={requiredCycles} onChange={(event) => setRequiredCycles(Number(event.target.value))}>
              {[1, 2, 3].map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </Field>
          <Field label="C-rate" hint="Sets the longest allowed charge or discharge.">
            <select className="input" value={cRate} onChange={(event) => setCRate(Number(event.target.value))}>
              {C_RATES.map(([rate, hours]) => (
                <option key={rate} value={rate}>
                  {rate} C (max {hours} h)
                </option>
              ))}
            </select>
          </Field>
          <Field label="Minimum SOC swing (%)">
            <input className="input" type="number" min={50} max={100} step="0.5" value={minSwing} onChange={(event) => setMinSwing(event.target.value)} />
          </Field>
          <Field label="SOC deviation threshold (%)">
            <input className="input" type="number" min={0.5} step="0.5" value={threshold} onChange={(event) => setThreshold(event.target.value)} />
          </Field>
        </div>
      </section>

      <section className="card stack">
        <div className="card-head">
          <div>
            <h3>Programme year and commitments</h3>
            <p>The result is compared with the committed values of this year.</p>
          </div>
        </div>
        <Field label="Reporting year">
          <select className="input" style={{ maxWidth: 360 }} value={year ?? "auto"} onChange={(event) => setYear(event.target.value === "auto" ? null : Number(event.target.value))}>
            <option value="auto">Automatic (from the SAT window)</option>
            {years.map((value) => (
              <option key={value} value={value}>
                {yearLabel(value)}
              </option>
            ))}
          </select>
        </Field>
        <CommitmentsPanel pid={pid} year={year} editable={can(me.data, "commitments.edit")} />
      </section>

      <section className="card stack">
        <div className="card-head">
          <div>
            <h3>Battery layout</h3>
            <p>Sections, labels and logger clock corrections. Saved per project; every version is kept.</p>
          </div>
        </div>
        {layout.isLoading ? (
          <Spinner label="Loading the layout" />
        ) : layout.error && !layoutMissing ? (
          <ErrorNotice error={layout.error} />
        ) : (
          <LayoutPanel pid={pid} manifest={manifest} saved={saved} onSaved={() => setError(null)} />
        )}
      </section>

      <ErrorNotice error={error} title="The analysis could not run" />
      <div className="row end">
        {running && <Spinner label="Analysing cycles, sections and alarms" />}
        {!saved && !layout.isLoading && <span className="muted small">Save the layout to run the analysis.</span>}
        <button className="btn primary" disabled={!canRun} onClick={run}>
          Run the assessment →
        </button>
      </div>
    </div>
  );
}
